import {
  createListenerMiddleware,
  isAnyOf,
  type TypedStartListening,
} from "@reduxjs/toolkit";

import { cartApi, selectCartData } from "@/src/features/cart/cartApi";
import { profileQueryApi } from "@/src/features/profile/profileApi";
import type { AppDispatch, RootState } from "@/src/store";
import { baseApi } from "@/src/store/baseApi";
import { clearCredentials, setCredentials } from "@/src/store/slices/authSlice";
import { resetUiState } from "@/src/store/slices/uiSlice";

export const listenerMiddleware = createListenerMiddleware();

export type AppStartListening = TypedStartListening<RootState, AppDispatch>;

export const startAppListening =
  listenerMiddleware.startListening as AppStartListening;

async function prefetchAuthenticatedSessionData(dispatch: AppDispatch) {
  const cartRequest = dispatch(
    cartApi.endpoints.getCart.initiate(undefined, { forceRefetch: true }),
  );
  const profileRequest = dispatch(
    profileQueryApi.endpoints.getProfile.initiate(undefined, {
      forceRefetch: true,
    }),
  );

  await Promise.allSettled([
    cartRequest.unwrap(),
    profileRequest.unwrap(),
  ]);

  cartRequest.unsubscribe();
  profileRequest.unsubscribe();
}

async function mergeGuestCartIntoServer(
  dispatch: AppDispatch,
  guestCartItems: Array<{ variantId: number; quantity: number }>,
  authenticatedCart: ReturnType<typeof selectCartData>,
) {
  if (guestCartItems.length === 0 || authenticatedCart?.type !== "user") {
    return;
  }

  const userVariantIds = new Set(
    authenticatedCart.items.map((item) => item.variantId),
  );
  const missingGuestItems = guestCartItems.filter(
    (item) => !userVariantIds.has(item.variantId),
  );

  if (missingGuestItems.length === 0) {
    return;
  }

  await Promise.allSettled(
    missingGuestItems.flatMap((item) =>
      Array.from({ length: item.quantity }, () =>
        dispatch(cartApi.endpoints.addToCart.initiate({ variantId: item.variantId })).unwrap(),
      ),
    ),
  );

  dispatch(cartApi.util.invalidateTags([{ type: "Cart", id: "CURRENT" }]));
}
startAppListening({
  actionCreator: setCredentials,
  effect: async (_action, listenerApi) => {
    // A token refresh also updates credentials. It is not a new sign-in and
    // must not restart the guest-to-user synchronization cycle.
    if (listenerApi.getOriginalState().auth.isAuthenticated) {
      return;
    }
    const state = listenerApi.getState();
    const cachedCart = selectCartData(state);
    const guestCartItems =
      cachedCart?.type === "guest"
        ? cachedCart.items.map(({ variantId, quantity }) => ({ variantId, quantity }))
        : [];
    await prefetchAuthenticatedSessionData(listenerApi.dispatch);
    await mergeGuestCartIntoServer(
      listenerApi.dispatch,
      guestCartItems,
      selectCartData(listenerApi.getState()),
    );
  },
});

startAppListening({
  matcher: isAnyOf(clearCredentials),
  effect: async (_action, listenerApi) => {
    listenerApi.dispatch(baseApi.util.resetApiState());
    listenerApi.dispatch(resetUiState());
  },
});
